// Desktop Models Section - Local AI model management
// Subpage for managing local AI models available in the desktop app

import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LoadingPanel, RevoltButton, RevoltTabs, RevoltInput, StepProgress, RevoltColorPicker, DropdownSelect, RevoltDropdownButton } from '../../../../components';
import { DesktopModelsManager } from './managers/desktop-models-manager';
import { useManager } from '../../../../core';
import { logger } from '../../../../core';
import { DesktopHeader } from '../../components';
import { ModelsGrid, ModelDetailsModal, DeleteConfirmationModal, RecommendedModelDownloadModal, RecommendedModelsGrid, DesktopModelsContextMenu, HubModelsGrid, HubRepoDetailModal } from './components';
import { useAuth } from '../../../../contexts/AuthContext';
import './DesktopModelsSection.css';

const HUB_DISCOVER_SORT_OPTIONS = [
  { value: 'trending', label: 'Trending' },
  { value: 'downloads', label: 'Most downloads' },
  { value: 'likes', label: 'Most likes' },
  { value: 'lastModified', label: 'Recently updated' },
  { value: 'createdAt', label: 'Recently created' },
];

const HUB_DISCOVER_TASK_OPTIONS = [
  { value: '', label: 'Any task' },
  { value: 'text-generation', label: 'Text generation' },
  { value: 'text2text-generation', label: 'Text-to-text' },
  { value: 'image-text-to-text', label: 'Image-text-to-text' },
  { value: 'feature-extraction', label: 'Feature extraction' },
];

function hsvToRgbHex(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (h >= 0 && h < 60) {
    r1 = c; g1 = x; b1 = 0;
  } else if (h >= 60 && h < 120) {
    r1 = x; g1 = c; b1 = 0;
  } else if (h >= 120 && h < 180) {
    r1 = 0; g1 = c; b1 = x;
  } else if (h >= 180 && h < 240) {
    r1 = 0; g1 = x; b1 = c;
  } else if (h >= 240 && h < 300) {
    r1 = x; g1 = 0; b1 = c;
  } else {
    r1 = c; g1 = 0; b1 = x;
  }

  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  const toHex = (n) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function pickRandomModelColor() {
  // Saturated + bright: matches the “HSL: saturation 100%, brightness 100%” vibe.
  const hue = Math.random() * 360;
  return hsvToRgbHex(hue, 1, 1);
}


export function DesktopModelsSection() {
  const { manager, isInitialized } = useManager(DesktopModelsManager);
  const { isAdmin, membershipDetails, user } = useAuth();
  const isAdminForDesktopModels =
    !!isAdmin ||
    user?.role === 'admin' ||
    membershipDetails?.role === 'admin' ||
    membershipDetails?.role === 'site_admin';
  const [activeTab, setActiveTab] = useState('main');
  const [selectedModel, setSelectedModel] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [models, setModels] = useState([]);
  const [recommendedModels, setRecommendedModels] = useState([]);
  const [recommendedLoading, setRecommendedLoading] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedRecommendedModel, setSelectedRecommendedModel] = useState(null);
  const [importName, setImportName] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importColor, setImportColor] = useState(null);
  const [importStep, setImportStep] = useState(0);
  const [validatedSteps, setValidatedSteps] = useState([]);

  const [contextMenuState, setContextMenuState] = useState({
    isOpen: false,
    x: 0,
    y: 0,
    model: null,
  });

  // --- Hugging Face Discover state ---
  // Mirrors the huggingface.co/models page shape (skill: huggingface-hub-model-search):
  // a search query + a handful of filter/sort knobs, backed by the models
  // manager's hubSearchModels() proxy.
  const [hubQuery, setHubQuery] = useState('');
  const [hubSort, setHubSort] = useState('trending'); // trending | downloads | likes | lastModified | createdAt
  const [hubTask, setHubTask] = useState(''); // pipeline_tag filter; empty = any
  const [hubResults, setHubResults] = useState([]);
  const [hubTotalCount, setHubTotalCount] = useState(null);
  const [hubSearching, setHubSearching] = useState(false);
  const [hubError, setHubError] = useState(null);
  const [hubRepoOpen, setHubRepoOpen] = useState(false);
  const [hubRepoSelected, setHubRepoSelected] = useState(null);
  const [hubHasSearched, setHubHasSearched] = useState(false);

  const caseInsensitiveDisplayName = useCallback((m) => {
    const name = m?.displayName;
    return typeof name === 'string' ? name.trim().toLowerCase() : '';
  }, []);

  const sortModelsAlphabetical = useCallback((list) => {
    const safe = Array.isArray(list) ? list : [];
    // Keep sort stable-ish by only comparing displayName.
    return [...safe].sort((a, b) => {
      const an = caseInsensitiveDisplayName(a);
      const bn = caseInsensitiveDisplayName(b);
      if (an === bn) return 0;
      return an < bn ? -1 : 1;
    });
  }, [caseInsensitiveDisplayName]);

  const enrichModelsWithMissingFiles = useCallback(async (modelsList) => {
    const list = Array.isArray(modelsList) ? modelsList : [];

    // In case we're ever rendered outside desktop/Electron, do not attempt IPC checks.
    if (!manager?.validateModelPath) {
      return list.map((m) => ({ ...m, fileMissing: false }));
    }

    return await Promise.all(
      list.map(async (m) => {
        if (!m || m.mode !== 'file' || !m.path) return { ...m, fileMissing: false };
        try {
          const exists = await manager.validateModelPath(m.path, 'file');
          return { ...m, fileMissing: !exists };
        } catch {
          // If validation fails for any reason, fall back to "not missing" so details remain usable.
          return { ...m, fileMissing: false };
        }
      })
    );
  }, [manager]);

  const refreshModelsWithMissingFiles = async (preserveSelectedId) => {
    const updatedModels = manager.getModels();
    const enriched = await enrichModelsWithMissingFiles(updatedModels);

    setModels(enriched);

    if (preserveSelectedId) {
      const preserved = enriched.find((m) => m.id === preserveSelectedId) || null;
      setSelectedModel(preserved);
      return preserved;
    }

    return null;
  };

  // Sync models state with manager on initialization
  useEffect(() => {
    let cancelled = false;

    if (isInitialized && manager) {
      (async () => {
        const updatedModels = manager.getModels();
        const enriched = await enrichModelsWithMissingFiles(updatedModels);
        if (cancelled) return;
        setModels(enriched);
      })();
    }

    return () => {
      cancelled = true;
    };
  }, [isInitialized, manager, enrichModelsWithMissingFiles]);

  // Load recommended models for Discover tab.
  useEffect(() => {
    if (!isInitialized || !manager?.getRecommendedModels) return;

    let cancelled = false;
    setRecommendedLoading(true);

    manager.getRecommendedModels()
      .then((list) => {
        if (cancelled) return;
        setRecommendedModels(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Failed to load recommended models:', e);
        setRecommendedModels([]);
      })
      .finally(() => {
        if (cancelled) return;
        setRecommendedLoading(false);
      });

    return () => { cancelled = true; };
  }, [isInitialized, manager]);

  // Invalidate future steps when model name changes (user edited it)
  useEffect(() => {
    if (importStep === 1 && validatedSteps.includes(2)) {
      // If we're on step 1 and step 2 was validated, invalidate it
      setValidatedSteps(prev => prev.filter(step => step !== 2));
    }
  }, [importName, importStep, validatedSteps]);

  const runHubSearch = useCallback(async () => {
    if (!manager?.hubSearchModels) return;
    setHubSearching(true);
    setHubError(null);
    setHubHasSearched(true);
    try {
      // Always scope to gguf + llama.cpp compatible, matching the user's
      // reference URL (huggingface.co/models?library=gguf&apps=llama.cpp).
      const tags = ['gguf'];
      const res = await manager.hubSearchModels({
        query: hubQuery.trim(),
        tags,
        task: hubTask || null,
        sort: hubSort,
        direction: -1,
        limit: 30,
      });
      setHubResults(Array.isArray(res?.models) ? res.models : []);
      setHubTotalCount(typeof res?.totalCount === 'number' ? res.totalCount : null);
    } catch (e) {
      setHubError(e?.message || 'Search failed');
      setHubResults([]);
      setHubTotalCount(null);
    } finally {
      setHubSearching(false);
    }
  }, [manager, hubQuery, hubTask, hubSort]);

  const handleOpenHubRepo = useCallback((repoCard) => {
    setHubRepoSelected(repoCard);
    setHubRepoOpen(true);
  }, []);

  const handleToggleModel = async (modelId) => {
    try {
      const model = manager.getModelById(modelId);
      if (model) {
        await manager.updateModel(modelId, { isActive: !model.isActive });
        const preserveSelectedId = selectedModel?.id === modelId ? modelId : null;
        await refreshModelsWithMissingFiles(preserveSelectedId);
      }
    } catch (error) {
      console.error('Failed to toggle model:', error);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenuState({
      isOpen: false,
      x: 0,
      y: 0,
      model: null,
    });
  };

  const handleModelContextMenu = (model, event) => {
    if (!model || !event) return;
    setContextMenuState({
      isOpen: true,
      x: event.clientX,
      y: event.clientY,
      model,
    });
  };

  const handleConfigureModel = async (modelId) => {
    try {
      await manager.configureModel(modelId);
    } catch (error) {
      console.error('Failed to configure model:', error);
    }
  };

  const handleDeleteModel = (modelId) => {
    const model = manager.getModelById(modelId);
    if (model) {
      setModelToDelete(model);
      setDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = async (modelId, deleteFile) => {
    try {
      await manager.deleteModel(modelId, deleteFile);

      // Close modals and refresh state
      setDeleteModalOpen(false);
      setModelToDelete(null);

      // Close details modal if it was showing the deleted model
      if (selectedModel && selectedModel.id === modelId) {
        setDetailsModalOpen(false);
        setSelectedModel(null);
      }

      // Refresh local models state
      await refreshModelsWithMissingFiles(null);
    } catch (error) {
      console.error('Failed to delete model:', error);
    }
  };

  const handleUpdatePath = async (modelId, file) => {
    if (!file) return;

    try {
      await manager.updateModel(modelId, {
        path: file.path || file.name,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      const preserveSelectedId = selectedModel?.id === modelId ? modelId : null;
      await refreshModelsWithMissingFiles(preserveSelectedId);
    } catch (error) {
      console.error('Failed to update model path:', error);
    }
  };

  const handleUpdateName = async (modelId, newName) => {
    if (!newName.trim()) return;

    try {
      await manager.updateModel(modelId, {
        displayName: newName.trim(),
        updatedAt: new Date().toISOString()
      });

      // Update local state
      const preserveSelectedId = selectedModel?.id === modelId ? modelId : null;
      await refreshModelsWithMissingFiles(preserveSelectedId);
    } catch (error) {
      console.error('Failed to update model name:', error);
    }
  };

  const handleUpdateModelColor = async (modelId, color) => {
    try {
      if (!modelId) return;

      await manager.updateModel(modelId, {
        color,
        updatedAt: new Date().toISOString()
      });

      await refreshModelsWithMissingFiles(selectedModel?.id === modelId ? modelId : null);
    } catch (error) {
      console.error('Failed to update model color:', error);
    }
  };

  // Handle step navigation with validation
  const handleStepChange = (newStep) => {
    // Only allow navigation to validated steps
    if (validatedSteps.includes(newStep)) {
      setImportStep(newStep);
    }
  };

  // Invalidate future steps when current step data changes
  const invalidateFutureSteps = (currentStepIndex) => {
    setValidatedSteps(prev => prev.filter(step => step <= currentStepIndex));
  };

  const handleImportModel = async () => {
    if (!importName.trim() || !importFile) return;

    try {
      await manager.addModel({
        displayName: importName.trim(),
        mode: 'file',
        path: importFile.path || importFile.name,
        color: importColor,
      });

      // Clear form and reset to initial state
      setImportName('');
      setImportFile(null);
      setImportColor(null);
      setImportStep(0);
      setValidatedSteps([]);

      // Refresh local models state
      await refreshModelsWithMissingFiles(null);

      // Switch to main tab to show the newly imported model
      setActiveTab('main');
    } catch (error) {
      console.error('Failed to import model:', error);
    }
  };

  const extractDisplayNameFromGguf = (ggufInfo) => {
    const general = ggufInfo?.metadata?.general || null;
    const architecture = general?.architecture || null;

    switch (architecture) {
      case 'llama':
        // Prefer the human-friendly base model name when present; otherwise fall back to general name.
        return (
          general?.base_model?.['0']?.name ||
          general?.base_model?.[0]?.name ||
          general?.name ||
          null
        );
      case 'gpt-oss':
        return general?.name || null;
      case 'qwen3':
        return general?.name || null;
      default:
        return general?.name || null;
    }
  };

  // Validate if file is a real GGUF file by parsing its metadata (desktop only)
  const validateGGUF = async (file) => {
    if (!file) return { ok: false, displayName: null };

    // Check file extension first (fast)
    if (!file.name.toLowerCase().endsWith('.gguf')) {
      return { ok: false, displayName: null };
    }

    // In the desktop app we can validate by reading GGUF metadata
    if (typeof window !== 'undefined' && window.llamaCppAPI?.getMetadata) {
      const parsed = await window.llamaCppAPI.getMetadata(file.path);
      if (!parsed?.success || !parsed?.metadata) {
        return { ok: false, displayName: null };
      }

      const displayName = extractDisplayNameFromGguf(parsed.metadata);
      return { ok: true, displayName: displayName || null };
    }

    // Fallback: accept by extension (non-desktop / missing API)
    return { ok: true, displayName: null };
  };

  const handleFileSelect = async () => {
    // Use Electron dialog for proper absolute path
    if (typeof window !== 'undefined' && window.dialogAPI?.showOpenDialog) {
      try {
        const result = await window.dialogAPI.showOpenDialog({
          title: 'Select GGUF Model File',
          properties: ['openFile'],
          filters: [
            { name: 'GGUF Files', extensions: ['gguf'] },
            { name: 'All Files', extensions: ['*'] }
          ]
        });

        if (result.canceled || !result.filePaths || result.filePaths.length === 0) {
          return;
        }

        const filePath = result.filePaths[0];

        // Validate the file
        const validation = await validateGGUF({ path: filePath, name: filePath.split(/[/\\]/).pop() });

        if (validation.ok) {
          // Create a file-like object with the path
          const fileObject = {
            path: filePath,
            name: filePath.split(/[/\\]/).pop(),
            size: 0, // We'll get this from the backend validation
          };

          setImportFile(fileObject);
          // Auto-populate name from GGUF metadata when available; fallback to filename
          const nameWithoutExt = fileObject.name.replace(/\.[^/.]+$/, '');
          const nextName = (validation.displayName || '').trim() || nameWithoutExt;
          setImportName(nextName);

          // Validate step 1 and auto-advance to step 2
          setValidatedSteps([0, 1]); // Mark steps 0 and 1 as validated
          setImportStep(1);
          setImportColor(pickRandomModelColor());
        } else {
          // Clear previous validations if file is invalid
          setImportFile(null);
          setValidatedSteps([]); // Invalidate all steps
          setImportName('');
          setImportColor(null);

          // Could show error message here
          alert('Please select a valid GGUF model file.');
        }
      } catch (error) {
        console.error('Failed to open file dialog:', error);
        alert('Failed to open file dialog. Please try again.');
      }
    } else {
      // Fallback for non-Electron environments
      alert('File selection is only available in the desktop application.');
    }
  };

  const handleCardClick = (model) => {
    setSelectedModel(model);
    setDetailsModalOpen(true);
  };

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--desktop">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Models"
            subtitle="Loading local AI models..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  const filteredModels = models.filter((model) => {
    const name = typeof model?.displayName === 'string' ? model.displayName : '';
    return name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const activeModels = filteredModels.filter((m) => !!m?.isActive);
  const inactiveModels = filteredModels.filter((m) => !m?.isActive);
  const orderedModels = [
    ...sortModelsAlphabetical(activeModels),
    ...sortModelsAlphabetical(inactiveModels)
  ];

  logger.log({
    tags: "section|desktop|models|render",
    color1: "lime"
  }, `DesktopModelsSection rendering with ${models.length} models`);

  const tabs = [
    {
      id: 'main',
      label: 'My Models',
      icon: 'brain',
      content: (
        <div className="desktop-tab-content">
          {/* Search bar */}
          {models.length > 0 && (
            <div className="desktop-models-search">
              <div className="desktop-search-input-container">
                <i className="fa-solid fa-magnifying-glass desktop-search-icon"></i>
                <RevoltInput
                  type="text"
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="desktop-search-input"
                />
              </div>
            </div>
          )}

          <ModelsGrid
            models={orderedModels}
            onCardClick={handleCardClick}
            onOpenContextMenu={handleModelContextMenu}
          />
        </div>
      )
    },
    {
      id: 'discover',
      label: 'Discover',
      icon: 'search',
      content: (
        <div className="desktop-tab-content">
          <div className="desktop-discover-hub-section">
            <div className="desktop-discover-hub-header">
              <h4>Discover models on Hugging Face</h4>
              <p>
                Search the Hugging Face Hub for GGUF models compatible with llama.cpp.
                Click a result to view its files and download a single-file GGUF into
                your local models.
              </p>
            </div>

            <div className="desktop-discover-hub-controls">
              <div className="desktop-search-input-container">
                <i className="fa-solid fa-magnifying-glass desktop-search-icon"></i>
                <RevoltInput
                  type="text"
                  placeholder="Search Hugging Face (e.g. qwen, llama, gemma)"
                  value={hubQuery}
                  onChange={(e) => setHubQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') runHubSearch();
                  }}
                  className="desktop-search-input"
                />
              </div>

              <div className="desktop-discover-hub-dropdown-wrap">
                <DropdownSelect
                  id="desktop-models-discover-sort"
                  name="hubSort"
                  value={hubSort}
                  options={HUB_DISCOVER_SORT_OPTIONS}
                  onChange={(e) => setHubSort(e.target.value)}
                  placeholder="Sort by…"
                  size="sm"
                  className="desktop-discover-hub-dropdown-trigger"
                />
              </div>

              <div className="desktop-discover-hub-dropdown-wrap">
                <DropdownSelect
                  id="desktop-models-discover-task"
                  name="hubTask"
                  value={hubTask}
                  options={HUB_DISCOVER_TASK_OPTIONS}
                  onChange={(e) => setHubTask(e.target.value)}
                  placeholder="Task…"
                  size="sm"
                  className="desktop-discover-hub-dropdown-trigger"
                />
              </div>

              <RevoltDropdownButton
                title="Search Hugging Face"
                showDropdown={false}
                onClick={runHubSearch}
                disabled={hubSearching}
                className="desktop-discover-hub-search-btn"
              >
                <i className={`fa-solid ${hubSearching ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'}`} />
                {hubSearching ? 'Searching…' : 'Search'}
              </RevoltDropdownButton>
            </div>

            {hubTotalCount != null && (
              <div className="desktop-discover-hub-total">
                About {hubTotalCount.toLocaleString()} models match your filters
              </div>
            )}

            {hubError && (
              <div className="desktop-discover-hub-error">
                <i className="fa-solid fa-triangle-exclamation" /> {hubError}
              </div>
            )}

            {hubHasSearched ? (
              <HubModelsGrid
                models={hubResults}
                onCardClick={handleOpenHubRepo}
                emptyMessage={hubSearching ? 'Searching…' : 'No results for those filters.'}
              />
            ) : (
              <div className="desktop-models-grid-empty">
                <div className="desktop-empty-icon">
                  <i className="fa-solid fa-search" />
                </div>
                <h3>Search Hugging Face</h3>
                <p>Type a query above and press Enter to browse GGUF models from the Hub.</p>
              </div>
            )}
          </div>

          <div className="desktop-discover-recommended-section">
            <h4>Recommended models</h4>
            <p>Hand-picked models we suggest for a first-time local setup.</p>

            {recommendedLoading ? (
              <div className="desktop-models-grid-empty">
                <div className="desktop-empty-icon">
                  <i className="fa-solid fa-spinner fa-spin" />
                </div>
                <h3>Loading Recommended Models</h3>
                <p>Preparing download options…</p>
              </div>
            ) : (
              <RecommendedModelsGrid
                models={recommendedModels}
                onCardClick={(m) => {
                  setSelectedRecommendedModel(m);
                  setDownloadModalOpen(true);
                }}
              />
            )}
          </div>
        </div>
      )
    },
    {
      id: 'import',
      label: 'Import',
      icon: 'upload',
      content: (
        <div className="desktop-tab-content">
          {/* Step Progress Indicator */}
          <StepProgress
            steps={[
              { label: 'Select File' },
              { label: 'Model Info' },
              { label: 'Import' }
            ]}
            currentStep={importStep}
            validatedSteps={validatedSteps}
            onStepClick={handleStepChange}
            linear={false}
          />

          {/* Step Content */}
          <div className="desktop-import-step-content">
            {importStep === 0 && (
              <div className="desktop-import-step">
                <h4>Step 1: Select Model File</h4>
                <p>Choose the GGUF model file you want to import from your computer.</p>

                <div className="desktop-form-group">
                  <label>Model File</label>
                  <div className="desktop-file-input">
                    <RevoltButton
                      variant="outline"
                      onClick={handleFileSelect}
                    >
                      <i className="fa-solid fa-folder-open"></i>
                      {importFile ? importFile.name : 'Choose File'}
                    </RevoltButton>
                  </div>
                  <p className="desktop-form-help">
                    Select a GGUF model file from your computer.
                  </p>
                </div>

                <div className="desktop-step-instructions">
                  <p>Select a valid GGUF model file to continue to the next step.</p>
                </div>
              </div>
            )}

            {importStep === 1 && (
              <div className="desktop-import-step">
                <h4>Step 2: Model Information</h4>
                <p>Review and edit the model information extracted from your file.</p>

                <div className="desktop-form-group">
                  <label htmlFor="model-name">Model Name</label>
                  <RevoltInput
                    id="model-name"
                    type="text"
                    value={importName}
                    onChange={(e) => setImportName(e.target.value)}
                    placeholder="Enter a friendly name for your model"
                    required
                  />
                  <p className="desktop-form-help">
                    This name was auto-generated from your file. You can edit it if needed.
                  </p>
                </div>

                <div className="desktop-form-group">
                  <label>Model Color</label>
                  <RevoltColorPicker
                    value={importColor}
                    onChange={(color) => setImportColor(color)}
                  />
                  <p className="desktop-form-help">
                    Choose a custom tint for this model. Selecting transparent removes the custom color.
                  </p>
                </div>

                <div className="desktop-step-actions">
                  <RevoltButton
                    variant="ghost"
                    onClick={() => {
                      setImportStep(0);
                      invalidateFutureSteps(0);
                      setImportColor(null);
                    }}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                    Back
                  </RevoltButton>
                  <RevoltButton
                    variant="primary"
                    onClick={() => {
                      // Validate step 2 and proceed to step 3
                      setValidatedSteps(prev => {
                        const newValidated = [...prev];
                        if (!newValidated.includes(2)) {
                          newValidated.push(2);
                        }
                        return newValidated;
                      });
                      setImportStep(2);
                    }}
                    disabled={!importName.trim()}
                  >
                    Next: Import
                    <i className="fa-solid fa-arrow-right"></i>
                  </RevoltButton>
                </div>
              </div>
            )}

            {importStep === 2 && (
              <div className="desktop-import-step">
                <h4>Step 3: Import Model</h4>
                <p>Review your model details and complete the import.</p>

                <div className="desktop-import-preview">
                  <div className="desktop-preview-header">
                    <h5>Model Preview</h5>
                    <p>Here's what will be imported:</p>
                  </div>

                  <div className="desktop-preview-content">
                    <div className="desktop-preview-item">
                      <div className="desktop-preview-label">Model Name</div>
                      <div className="desktop-preview-value">{importName}</div>
                    </div>
                    <div className="desktop-preview-item">
                      <div className="desktop-preview-label">File Name</div>
                      <div className="desktop-preview-value">{importFile?.name}</div>
                    </div>
                    <div className="desktop-preview-item">
                      <div className="desktop-preview-label">File Size</div>
                      <div className="desktop-preview-value">
                        {importFile ? `${(importFile.size / (1024 * 1024)).toFixed(2)} MB` : 'N/A'}
                      </div>
                    </div>
                    <div className="desktop-preview-item">
                      <div className="desktop-preview-label">File Type</div>
                      <div className="desktop-preview-value">GGUF Model</div>
                    </div>
                  </div>
                </div>

                <div className="desktop-step-actions">
                  <RevoltButton
                    variant="ghost"
                    onClick={() => {
                      setImportStep(1);
                      invalidateFutureSteps(1);
                    }}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                    Back
                  </RevoltButton>
                  <RevoltButton
                    variant="primary"
                    onClick={handleImportModel}
                    disabled={!importName.trim() || !importFile}
                  >
                    <i className="fa-solid fa-upload"></i>
                    Import Model
                  </RevoltButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="desktop-layout">
      {/* Full-width desktop header below main header */}
      <DesktopHeader activePage="models" />

      {/* Main content with layout */}
      <MainLayout contentClassName="main-content--desktop">
        <div className="desktop-models-container">
          <div className="desktop-section-content">
            {/* Tabs */}
            <RevoltTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </div>
      </MainLayout>

      <ModelDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedModel(null);
        }}
        isAdmin={!!isAdminForDesktopModels}
        model={selectedModel}
        onToggle={handleToggleModel}
        onConfigure={handleConfigureModel}
        onDelete={handleDeleteModel}
        onUpdatePath={handleUpdatePath}
        onUpdateName={handleUpdateName}
        onUpdateColor={handleUpdateModelColor}
      />

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setModelToDelete(null);
        }}
        model={modelToDelete}
        onConfirmDelete={handleConfirmDelete}
      />

      <HubRepoDetailModal
        isOpen={hubRepoOpen}
        onClose={() => {
          setHubRepoOpen(false);
          setHubRepoSelected(null);
        }}
        repo={hubRepoSelected}
        manager={manager}
        onViewModel={async (importedModel) => {
          try {
            await manager.refreshModels?.();
            const match = await refreshModelsWithMissingFiles(importedModel?.id);
            setDetailsModalOpen(!!match);
            setActiveTab('main');
          } catch (e) {
            console.error('Failed to refresh after Hub download:', e);
          }
        }}
      />

      <RecommendedModelDownloadModal
        isOpen={downloadModalOpen}
        onClose={() => {
          setDownloadModalOpen(false);
          setSelectedRecommendedModel(null);
        }}
        recommendedModel={selectedRecommendedModel}
        onViewModel={async (importedModel) => {
          try {
            // Ensure newly created model appears in "My Models" and open its details.
            await manager.refreshModels?.();
            const match = await refreshModelsWithMissingFiles(importedModel?.id);
            setDetailsModalOpen(!!match);
            setActiveTab('main');
          } catch (e) {
            console.error('Failed to refresh after recommended download:', e);
          }
        }}
      />

      {contextMenuState.isOpen && contextMenuState.model && (
        <DesktopModelsContextMenu
          anchorX={contextMenuState.x}
          anchorY={contextMenuState.y}
          model={contextMenuState.model}
          onClose={handleCloseContextMenu}
          onToggleActive={async (m) => {
            if (!m?.id) return;
            await handleToggleModel(m.id);
          }}
        />
      )}
    </div>
  );
}